import { useEffect, useState, useCallback } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { Eye, Star, Sparkles, Plus, Heart, BarChart3, Building2, ImageIcon } from "lucide-react";
import StarRating from "../components/StarRating";
import BusinessCard from "../components/BusinessCard";
import Pagination from "../components/Pagination";
import BusinessPhotosModal from "../components/BusinessPhotosModal";

const TABS = [
  { key: "businesses", label: "Mes entreprises", icon: Building2 },
  { key: "favorites", label: "Favoris", icon: Heart },
  { key: "analytics", label: "Analytics", icon: BarChart3 },
];

export default function DashboardPage() {
  const { user, loading: authLoading } = useAuth() || {};
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") || "businesses";

  const setTab = (k) => {
    const n = new URLSearchParams(params);
    n.set("tab", k);
    setParams(n, { replace: true });
  };

  useEffect(() => {
    if (!authLoading && !user) navigate("/login");
  }, [user, authLoading, navigate]);

  if (authLoading || !user) return null;

  return (
    <div className="min-h-screen bg-lokaso-sand px-4 py-10" data-testid="dashboard-page">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <h1 className="font-display text-3xl md:text-4xl font-bold">Bonjour, {user.full_name?.split(" ")[0] || "Pro"} 👋</h1>
            <p className="text-stone-600 mt-1">Tableau de bord — entreprises, favoris et statistiques.</p>
          </div>
          <Link to="/business/register" className="btn-primary inline-flex items-center gap-2" data-testid="dashboard-add-biz">
            <Plus className="w-4 h-4" /> Ajouter une entreprise
          </Link>
        </div>

        {/* Tabs */}
        <div className="mt-8 flex gap-1 border-b border-stone-200" data-testid="dashboard-tabs" role="tablist">
          {TABS.map(({ key, label, icon: Icon }) => (
            <button
              key={key}
              onClick={() => setTab(key)}
              role="tab"
              aria-selected={tab === key}
              className={`inline-flex items-center gap-2 px-4 py-3 text-sm font-semibold border-b-2 -mb-px transition ${
                tab === key
                  ? "border-lokaso-green text-lokaso-green"
                  : "border-transparent text-stone-500 hover:text-stone-800"
              }`}
              data-testid={`dashboard-tab-${key}`}
            >
              <Icon className="w-4 h-4" /> {label}
            </button>
          ))}
        </div>

        <div className="mt-6">
          {tab === "businesses" && <BusinessesTab />}
          {tab === "favorites" && <FavoritesTab />}
          {tab === "analytics" && <AnalyticsTab />}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Businesses tab
// ---------------------------------------------------------------------------
const PAGE_SIZE = 10;

function BusinessesTab() {
  const [data, setData] = useState({ items: [], total: 0, page: 1, total_pages: 1 });
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [photoModalBiz, setPhotoModalBiz] = useState(null);

  const load = useCallback(async (p) => {
    setLoading(true);
    try {
      const { data } = await api.get("/businesses/mine", { params: { page: p, limit: PAGE_SIZE } });
      setData(data);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(page); }, [page, load]);

  const items = data.items || [];

  // Aggregate stats across the visible page (close enough for header summary)
  const totals = items.reduce(
    (acc, b) => ({
      views: acc.views + (b.view_count || 0),
      reviews: acc.reviews + (b.review_count || 0),
      rating: acc.rating + (b.avg_rating || 0),
    }),
    { views: 0, reviews: 0, rating: 0 }
  );
  const avgRating = items.length > 0 ? (totals.rating / items.length) : 0;

  return (
    <div data-testid="dashboard-businesses-tab">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 mb-8" data-testid="dashboard-stats">
        <StatCard icon={<Eye className="w-5 h-5" />} label="Vues (page)" value={totals.views} color="#1A5C35" />
        <StatCard icon={<Star className="w-5 h-5" />} label="Avis (page)" value={totals.reviews} color="#E8A020" />
        <StatCard icon={<Sparkles className="w-5 h-5" />} label="Note moy. (page)" value={avgRating.toFixed(1)} color="#C4291C" />
        <StatCard icon={<Building2 className="w-5 h-5" />} label="Total entreprises" value={data.total} color="#1D4ED8" />
      </div>

      <div className="bg-white rounded-2xl border border-stone-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-stone-200 flex items-center justify-between flex-wrap gap-2">
          <h2 className="font-display text-xl font-bold">Mes entreprises</h2>
          <p className="text-xs text-stone-500" data-testid="dashboard-biz-count">
            {data.total} entreprise{data.total > 1 ? "s" : ""} · page {data.page} / {data.total_pages || 1}
          </p>
        </div>
        {loading ? (
          <div className="p-6 text-stone-500">Chargement…</div>
        ) : items.length === 0 ? (
          <div className="p-10 text-center">
            <p className="text-stone-600">Vous n'avez pas encore d'entreprise.</p>
            <Link to="/business/register" className="btn-green inline-block mt-4">Créer ma première entreprise</Link>
          </div>
        ) : (
          <>
            <table className="w-full text-sm">
              <thead className="bg-lokaso-cream text-stone-700">
                <tr>
                  <th className="text-left px-4 py-3">Nom</th>
                  <th className="text-left px-4 py-3 hidden md:table-cell">Statut</th>
                  <th className="text-left px-4 py-3 hidden md:table-cell">Plan</th>
                  <th className="text-left px-4 py-3 hidden md:table-cell">Vues</th>
                  <th className="text-left px-4 py-3">Note</th>
                  <th className="px-4 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {items.map((b) => (
                  <tr key={b.id} className="border-t border-stone-100" data-testid={`dashboard-row-${b.slug}`}>
                    <td className="px-4 py-3 font-semibold">{b.name}</td>
                    <td className="px-4 py-3 hidden md:table-cell"><StatusBadge status={b.status} /></td>
                    <td className="px-4 py-3 hidden md:table-cell capitalize">{b.plan}</td>
                    <td className="px-4 py-3 hidden md:table-cell">{b.view_count || 0}</td>
                    <td className="px-4 py-3"><StarRating rating={b.avg_rating} count={b.review_count} /></td>
                    <td className="px-4 py-3 text-right">
                      <div className="inline-flex gap-1">
                        <button
                          onClick={() => setPhotoModalBiz(b)}
                          className="px-2.5 py-1.5 rounded-lg border border-stone-200 hover:bg-stone-50 inline-flex items-center gap-1 text-xs"
                          data-testid={`dashboard-photos-${b.slug}`}
                          title="Gérer les photos"
                        >
                          <ImageIcon className="w-3.5 h-3.5" /> <span className="hidden sm:inline">Photos</span>
                        </button>
                        <Link
                          to={`/business/${b.slug}`}
                          className="px-2.5 py-1.5 rounded-lg bg-lokaso-green text-white text-xs font-semibold"
                          data-testid={`dashboard-view-${b.slug}`}
                        >
                          Voir
                        </Link>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="px-4 py-4 border-t border-stone-100">
              <Pagination page={data.page} totalPages={data.total_pages} onPageChange={setPage} />
            </div>
          </>
        )}
      </div>

      {photoModalBiz && (
        <BusinessPhotosModal
          business={photoModalBiz}
          onClose={() => setPhotoModalBiz(null)}
          onUpdated={() => load(page)}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Favorites tab
// ---------------------------------------------------------------------------
function FavoritesTab() {
  const [favs, setFavs] = useState([]);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/favorites");
      setFavs(data);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  return (
    <div data-testid="dashboard-favorites-tab">
      {loading ? (
        <div className="p-6 text-stone-500">Chargement…</div>
      ) : favs.length === 0 ? (
        <div className="bg-white rounded-2xl border border-stone-200 p-10 text-center">
          <Heart className="w-10 h-10 mx-auto text-lokaso-red mb-3" />
          <h3 className="font-display text-xl font-bold">Aucun favori pour le moment</h3>
          <p className="text-stone-600 mt-1">Cliquez sur le cœur d'une entreprise pour la retrouver ici.</p>
          <Link to="/search" className="btn-green inline-block mt-4">Explorer</Link>
        </div>
      ) : (
        <>
          <p className="text-sm text-stone-600 mb-4">{favs.length} entreprise{favs.length > 1 ? "s" : ""} sauvegardée{favs.length > 1 ? "s" : ""}.</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {favs.map((b) => <BusinessCard key={b.id} business={b} />)}
          </div>
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Analytics tab (advanced)
// ---------------------------------------------------------------------------
function AnalyticsTab() {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    (async () => {
      try {
        const { data } = await api.get("/businesses/mine/analytics", { params: { days } });
        setData(data);
      } finally { setLoading(false); }
    })();
  }, [days]);

  if (loading || !data) return <div className="p-6 text-stone-500">Chargement des statistiques…</div>;
  if (!data.businesses?.length) {
    return (
      <div className="bg-white rounded-2xl border border-stone-200 p-10 text-center">
        <BarChart3 className="w-10 h-10 mx-auto text-lokaso-green mb-3" />
        <h3 className="font-display text-xl font-bold">Aucune donnée disponible</h3>
        <p className="text-stone-600 mt-1">Ajoutez une entreprise pour commencer à voir des statistiques.</p>
      </div>
    );
  }

  const t = data.totals;

  return (
    <div className="space-y-6" data-testid="dashboard-analytics-tab">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h2 className="font-display text-xl font-bold">Statistiques avancées</h2>
        <div className="inline-flex rounded-xl border border-stone-200 bg-white overflow-hidden" data-testid="analytics-range">
          {[7, 30, 90].map((d) => (
            <button
              key={d}
              onClick={() => setDays(d)}
              className={`px-3 py-2 text-sm font-medium ${days === d ? "bg-lokaso-green text-white" : "text-stone-600 hover:bg-stone-50"}`}
              data-testid={`analytics-range-${d}`}
            >
              {d} jours
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 md:gap-4">
        <MetricCard label="Vues" value={t.views} color="#1A5C35" />
        <MetricCard label="Appels" value={t.calls} color="#C4291C" />
        <MetricCard label="WhatsApp" value={t.whatsapp} color="#10B981" />
        <MetricCard label="Maps" value={t.maps} color="#1D4ED8" />
        <MetricCard label="Conversion" value={`${t.conversion_pct}%`} color="#E8A020" hint="actions / vues" />
      </div>

      <Section title="Activité quotidienne">
        <DailyChart series={data.daily} />
      </Section>

      <div className="grid md:grid-cols-2 gap-6">
        <Section title="Par jour de la semaine">
          <DayOfWeekChart data={data.by_day_of_week} />
        </Section>
        <Section title="Par heure">
          <HourChart data={data.by_hour} />
        </Section>
      </div>
    </div>
  );
}

function StatCard({ icon, label, value, color }) {
  return (
    <div className="bg-white rounded-2xl border border-stone-200 p-4 md:p-5">
      <div className="flex items-center gap-2 text-stone-500 text-xs uppercase tracking-wider font-semibold">
        <span style={{ color }}>{icon}</span> {label}
      </div>
      <div className="mt-2 stat-num" style={{ color }}>{value}</div>
    </div>
  );
}

function MetricCard({ label, value, color, hint }) {
  return (
    <div className="bg-white rounded-2xl border border-stone-200 p-4">
      <div className="text-xs uppercase tracking-wider font-semibold text-stone-500">{label}</div>
      <div className="mt-1 stat-num !text-2xl" style={{ color }}>{value}</div>
      {hint && <div className="text-[11px] text-stone-400 mt-0.5">{hint}</div>}
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="bg-white rounded-2xl border border-stone-200 p-5">
      <h3 className="font-display text-lg font-bold mb-4">{title}</h3>
      {children}
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    active: { c: "bg-emerald-100 text-emerald-700", l: "Actif" },
    pending: { c: "bg-amber-100 text-amber-700", l: "En attente" },
    suspended: { c: "bg-rose-100 text-rose-700", l: "Suspendu" },
  };
  const v = map[status] || map.pending;
  return <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${v.c}`}>{v.l}</span>;
}

// ---------------------------------------------------------------------------
// Tiny SVG charts (no extra deps)
// ---------------------------------------------------------------------------
function DailyChart({ series }) {
  if (!series?.length) return <p className="text-sm text-stone-500">Aucune donnée.</p>;
  const max = Math.max(1, ...series.map((d) => d.total));
  const w = 720;
  const h = 160;
  const step = w / Math.max(1, series.length - 1);
  const pts = series.map((d, i) => [i * step, h - (d.total / max) * (h - 20) - 10]);
  const path = pts.map((p, i) => (i === 0 ? `M${p[0]},${p[1]}` : `L${p[0]},${p[1]}`)).join(" ");
  const area = `${path} L${w},${h} L0,${h} Z`;
  return (
    <div className="overflow-x-auto" data-testid="analytics-daily-chart">
      <svg viewBox={`0 0 ${w} ${h}`} className="w-full h-40">
        <defs>
          <linearGradient id="gradLkGreen" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#1A5C35" stopOpacity="0.35" />
            <stop offset="100%" stopColor="#1A5C35" stopOpacity="0" />
          </linearGradient>
        </defs>
        <path d={area} fill="url(#gradLkGreen)" />
        <path d={path} fill="none" stroke="#1A5C35" strokeWidth="2" />
        {pts.map((p, i) => (
          <circle key={`pt-${p[0].toFixed(0)}-${p[1].toFixed(0)}-${i}`} cx={p[0]} cy={p[1]} r={2} fill="#1A5C35" />
        ))}
      </svg>
      <div className="flex justify-between text-[10px] text-stone-400 mt-1">
        <span>{series[0]?.date}</span>
        <span>{series[series.length - 1]?.date}</span>
      </div>
    </div>
  );
}

function DayOfWeekChart({ data }) {
  const max = Math.max(1, ...data.map((d) => d.count));
  return (
    <div className="space-y-2" data-testid="analytics-dow-chart">
      {data.map((d) => (
        <div key={d.day} className="flex items-center gap-3 text-sm">
          <span className="w-24 text-stone-600">{d.day}</span>
          <div className="flex-1 h-3 bg-stone-100 rounded-full overflow-hidden">
            <div className="h-full bg-lokaso-green" style={{ width: `${(d.count / max) * 100}%` }} />
          </div>
          <span className="w-10 text-right font-semibold tabular-nums">{d.count}</span>
        </div>
      ))}
    </div>
  );
}

function HourChart({ data }) {
  const max = Math.max(1, ...data.map((d) => d.count));
  return (
    <div data-testid="analytics-hour-chart">
      <div className="flex items-end gap-0.5 h-32">
        {data.map((d) => (
          <div key={d.hour} className="flex-1 flex flex-col items-center gap-1 group">
            <div
              className="w-full bg-lokaso-gold/80 hover:bg-lokaso-gold rounded-t"
              style={{ height: `${(d.count / max) * 100}%`, minHeight: "2px" }}
              title={`${d.hour}h: ${d.count}`}
            />
          </div>
        ))}
      </div>
      <div className="flex justify-between text-[10px] text-stone-400 mt-2">
        <span>0h</span><span>6h</span><span>12h</span><span>18h</span><span>23h</span>
      </div>
    </div>
  );
}
